import React, { useState } from "react";
import { usePayGateway, PayButton, PayloftPop } from "@payloft/pay-react";

const TestLibraryPage = () => {
  const [reference, setReference] = useState("TEST-REF-12345");
  const [gatewayUrl, setGatewayUrl] = useState(
    typeof window !== "undefined" ? window.location.origin : "http://localhost:3000"
  );
  const [logs, setLogs] = useState([]);

  const addLog = (message, type = "info") => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs((prev) => [{ timestamp, message, type }, ...prev]);
  };

  const { open, isOpen, Modal } = usePayGateway({
    reference,
    gatewayUrl,
    onSuccess: (data) => {
      addLog(`Payment Success: ${JSON.stringify(data)}`, "success");
    },
    onClose: () => {
      addLog("Modal closed by user", "warning");
    },
    onError: (err) => {
      addLog(`Payment Error: ${JSON.stringify(err)}`, "error");
    },
    onReady: () => {
      addLog("Checkout IFrame ready", "info");
    },
  });

  const handleVanillaTrigger = () => {
    addLog("Triggering plain Vanilla PayloftPop.open({...})", "info");
    PayloftPop.open({
      reference,
      gatewayUrl,
      onSuccess: (data) => {
        addLog(`[Vanilla] Payment Success: ${JSON.stringify(data)}`, "success");
      },
      onClose: () => {
        addLog("[Vanilla] Modal closed by user", "warning");
      },
      onError: (err) => {
        addLog(`[Vanilla] Error: ${JSON.stringify(err)}`, "error");
      },
      onReady: () => {
        addLog("[Vanilla] IFrame ready", "info");
      },
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold mb-3">
            <span>🚀 Local Package Test</span>
            <span>•</span>
            <span>@payloft/pay-react</span>
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">
            Payloft React Library Test Suite
          </h1>
          <p className="text-slate-600 mt-2 text-sm">
            Interactive playground testing the embeddable hook, button, and vanilla script integration against your running gateway.
          </p>
        </div>

        {/* Configuration Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-6">
          <h2 className="text-base font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Test Parameters
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                Transaction Reference / ID
              </label>
              <input
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="e.g. 17482910472 or REF-123"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              />
              <span className="text-[11px] text-slate-400 mt-1 block">
                Pass any valid transaction reference id
              </span>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                Gateway Base URL
              </label>
              <input
                type="text"
                value={gatewayUrl}
                onChange={(e) => setGatewayUrl(e.target.value)}
                placeholder="http://localhost:3000"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              />
              <span className="text-[11px] text-slate-400 mt-1 block">
                Target Gateway UI instance (default: current origin)
              </span>
            </div>
          </div>
        </div>

        {/* Integration Trigger Buttons */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-6">
          <h2 className="text-base font-semibold text-slate-800 mb-4">
            Integration Triggers
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Hook Trigger */}
            <div className="flex flex-col gap-2 p-4 rounded-xl border border-slate-200 bg-slate-50/50 hover:bg-slate-50 transition">
              <span className="text-xs font-semibold text-slate-600">Method 1: Hook</span>
              <button
                type="button"
                onClick={open}
                className="w-full bg-slate-800 hover:bg-slate-900 text-white font-medium py-2.5 px-4 rounded-lg text-sm transition shadow-sm"
              >
                usePayGateway()
              </button>
              <span className="text-[11px] text-slate-500 text-center">
                Modal open: {isOpen ? "🟢 Yes" : "⚪ No"}
              </span>
            </div>

            {/* Declarative Component Trigger */}
            <div className="flex flex-col gap-2 p-4 rounded-xl border border-slate-200 bg-slate-50/50 hover:bg-slate-50 transition">
              <span className="text-xs font-semibold text-slate-600">Method 2: Component</span>
              <PayButton
                config={{
                  reference,
                  gatewayUrl,
                  onSuccess: (data) =>
                    addLog(`[PayButton] Success: ${JSON.stringify(data)}`, "success"),
                  onClose: () => addLog("[PayButton] Closed", "warning"),
                  onError: (err) => addLog(`[PayButton] Error: ${JSON.stringify(err)}`, "error"),
                  onReady: () => addLog("[PayButton] Ready", "info"),
                }}
                text="<PayButton />"
                style={{
                  backgroundColor: "#2563eb",
                  width: "100%",
                  padding: "10px 16px",
                  fontSize: "14px",
                  borderRadius: "8px",
                }}
              />
              <span className="text-[11px] text-slate-500 text-center">
                Pre-styled button component
              </span>
            </div>

            {/* Vanilla JS Trigger */}
            <div className="flex flex-col gap-2 p-4 rounded-xl border border-slate-200 bg-slate-50/50 hover:bg-slate-50 transition">
              <span className="text-xs font-semibold text-slate-600">Method 3: Vanilla Drop-in</span>
              <button
                type="button"
                onClick={handleVanillaTrigger}
                className="w-full bg-slate-800 hover:bg-slate-900 text-white font-medium py-2.5 px-4 rounded-lg text-sm transition shadow-sm"
              >
                PayloftPop.open()
              </button>
              <span className="text-[11px] text-slate-500 text-center">
                Plain JavaScript / HTML
              </span>
            </div>
          </div>
        </div>

        {/* Event Logs */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
              Live Event Stream
            </h2>
            {logs.length > 0 && (
              <button
                onClick={() => setLogs([])}
                className="text-xs text-slate-500 hover:text-slate-700 underline"
              >
                Clear logs
              </button>
            )}
          </div>

          <div className="bg-slate-900 rounded-xl p-4 min-h-[160px] max-h-[260px] overflow-y-auto font-mono text-xs text-slate-200 space-y-2">
            {logs.length === 0 ? (
              <div className="text-slate-500 italic text-center py-8">
                No events yet. Click one of the buttons above to trigger the checkout modal and inspect postMessage events.
              </div>
            ) : (
              logs.map((log, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <span className="text-slate-500 select-none">[{log.timestamp}]</span>
                  <span
                    className={
                      log.type === "success"
                        ? "text-emerald-400 font-semibold"
                        : log.type === "warning"
                          ? "text-amber-400"
                          : log.type === "error"
                            ? "text-rose-400 font-semibold"
                            : "text-slate-300"
                    }
                  >
                    {log.message}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Portal Modal from Hook */}
      <Modal />
    </div>
  );
};

export default TestLibraryPage;
